import { IconGauge } from '@tabler/icons-react';
import { conn as connection } from '../net/connection';
import { toast, useStore } from '../store';
import { Ic } from './ds';
import { Dropdown } from './Dropdown';

export type StreamPolicy = 'auto' | 'full' | 'low';

export const POLICIES: { value: StreamPolicy; label: string }[] = [
  { value: 'auto', label: 'Auto' },
  { value: 'full', label: 'Always full quality' },
  { value: 'low', label: 'Always low bandwidth' },
];
const SHORT: Record<StreamPolicy, string> = { auto: 'auto', full: 'full', low: 'low' };

/** The stream's frame rate, and its resolution when lowered: "60 fps", "30 fps · 75% resolution". */
export const streamRate = (net: { fps: number; scale: number }) =>
  [`${net.fps || 30} fps`, net.scale < 1 ? `${Math.round(net.scale * 100)}% resolution` : ''].filter(Boolean).join(' · ');

/**
 * The desktop stream on a bad network (deploy/vm/selkies-patch.py, BADNET): a chip while it runs at 30 fps, and
 * past that at a lower resolution, to keep up (canvas 4b "Desktop · View").
 */
export function LowBandwidth() {
  const net = useStore((s) => s.desktopNet);
  if (!net || net.mode !== 'low') return null;
  return (
    <span
      className="ws-chip"
      data-tone="attn"
      data-testid="desktop-lowbw"
      title={
        net.policy === 'low' && net.scale === 1
          ? 'Low bandwidth, as chosen: 30 fps'
          : net.scale < 1
            ? 'The link to this computer can’t carry the desktop even at 30 fps, so its picture is sent smaller and scaled up here; the desktop and its windows keep their size'
            : 'The link to this computer can’t carry the desktop at full rate, so it runs at 30 fps: fewer frames keep it responsive and text sharp'
      }
    >
      <Ic icon={IconGauge} size={14} />
      Low bandwidth · {streamRate(net)}
    </span>
  );
}

/** Asks the machine for a stream policy; says so when it can't. */
export const setStreamPolicy = (policy: StreamPolicy) =>
  void connection
    .control('desktop.netpolicy', { policy })
    .catch((err: Error) => toast({ title: '✗ Couldn’t change the network mode', body: err.message, kind: 'error' }, 6000));

/** For the controller, the policy: auto (drop the frame rate when the link can't carry it), always full
 *  quality, or always low bandwidth. */
export function NetworkMode() {
  const net = useStore((s) => s.desktopNet);
  if (!net) return null;
  return (
    <Dropdown<StreamPolicy>
      value={net.policy}
      shown={`Network: ${SHORT[net.policy]}`}
      items={POLICIES}
      heading="Network"
      label="Network mode"
      title="What the desktop gives up on a slow link: auto drops to 30 fps, then to a lower resolution, only when the link can’t keep up"
      testid="desktop-netpolicy"
      onChange={setStreamPolicy}
    />
  );
}
